import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import { pttService, PttUtterance } from '../services/pttService';

interface PttCaptionFeedProps {
  courseId?: string;
  /** Called when a located transmission is selected. */
  onZoomToLocation?: (lat: number, lng: number) => void;
  /** Called when the selected transmission is clicked again (deselected). */
  onClearZoom?: () => void;
}

const MAX_ITEMS = 200;
const SEARCH_LIMIT = 500;
const SEARCH_DEBOUNCE_MS = 350;
const ALL_TEAMS = '__all__';

const formatTime = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString();
};

const teamKey = (u: PttUtterance) => u.channel_id || u.team || '';
const teamLabel = (u: PttUtterance) => u.team || u.channel_id || '';

const teamOrder = (key: string) => {
  const n = parseInt(key.replace(/\D+/g, ''), 10);
  return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
};

const matches = (u: PttUtterance, needle: string) =>
  [u.transcript, u.sender, u.entity_id, u.device_id, u.team]
    .some((v) => (v || '').toLowerCase().includes(needle));

/** Full-height PTT transcript for the COP Messages tab. Newest first. */
const PttCaptionFeed: React.FC<PttCaptionFeedProps> = ({
  courseId,
  onZoomToLocation,
  onClearZoom,
}) => {
  const [items, setItems] = useState<PttUtterance[]>([]);
  const [query, setQuery] = useState('');
  const [team, setTeam] = useState<string>(ALL_TEAMS);
  const [serverHits, setServerHits] = useState<PttUtterance[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    pttService
      .listUtterances({ course_id: courseId, limit: MAX_ITEMS })
      .then((res) => {
        if (!cancelled) setItems(res.utterances);
      })
      .catch(() => {});
    const es = pttService.connectStream((utterance) => {
      setItems((prev) => [utterance, ...prev.filter((p) => p.id !== utterance.id)].slice(0, MAX_ITEMS));
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [courseId]);

  // The live list only holds the latest MAX_ITEMS; searches also ask the server
  // so older transmissions are found. Results merge with live matches below.
  useEffect(() => {
    const needle = query.trim();
    if (!needle) {
      setServerHits(null);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      pttService
        .listUtterances({
          course_id: courseId,
          q: needle,
          channel: team !== ALL_TEAMS ? team : undefined,
          limit: SEARCH_LIMIT,
        })
        .then((res) => {
          if (!cancelled) setServerHits(res.utterances);
        })
        .catch(() => {
          if (!cancelled) setServerHits(null);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, team, courseId]);

  const teams = useMemo(() => {
    const seen = new Map<string, string>();
    [...items, ...(serverHits || [])].forEach((u) => {
      const key = teamKey(u);
      if (key && !seen.has(key)) seen.set(key, teamLabel(u));
    });
    return Array.from(seen.entries()).sort((a, b) => teamOrder(a[0]) - teamOrder(b[0]));
  }, [items, serverHits]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let pool = items;
    if (needle && serverHits) {
      const byId = new Map<number, PttUtterance>();
      [...items, ...serverHits].forEach((u) => byId.set(u.id, u));
      pool = Array.from(byId.values()).sort(
        (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
      );
    }
    return pool.filter(
      (u) => (team === ALL_TEAMS || teamKey(u) === team) && (!needle || matches(u, needle)),
    );
  }, [items, serverHits, query, team]);

  const handleClick = (u: PttUtterance) => {
    const located = u.latitude != null && u.longitude != null;
    if (!located) return;
    if (selectedId === u.id) {
      setSelectedId(null);
      onClearZoom?.();
      return;
    }
    setSelectedId(u.id);
    onZoomToLocation?.(u.latitude as number, u.longitude as number);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ px: 1, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search transcript or sender"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setQuery('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <TextField
          select
          size="small"
          value={team}
          onChange={(e) => setTeam(e.target.value)}
          sx={{ minWidth: 112 }}
          inputProps={{ 'aria-label': 'Filter by team' }}
        >
          <MenuItem value={ALL_TEAMS}>All teams</MenuItem>
          {teams.map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ px: 1.25, pb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {visible.length} transmission{visible.length === 1 ? '' : 's'}
        </Typography>
        {selectedId != null && (
          <Typography variant="caption" color="text.secondary">
            Click the highlighted line again to zoom back out
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1, pb: 1 }}>
        {visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            {query || team !== ALL_TEAMS ? 'No transmissions match.' : 'No PTT transmissions yet.'}
          </Typography>
        ) : (
          visible.map((u) => {
            const located = u.latitude != null && u.longitude != null;
            const selected = u.id === selectedId;
            const who = u.sender || u.entity_id || u.device_id;
            return (
              <Tooltip
                key={u.id}
                title={located ? (selected ? 'Click to zoom back out' : 'Click to zoom to transmit location') : 'No location for this transmission'}
                placement="left"
                enterDelay={600}
              >
                <Box
                  onClick={() => handleClick(u)}
                  sx={{
                    mb: 0.75,
                    p: 1,
                    borderRadius: 1,
                    border: 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    cursor: located ? 'pointer' : 'default',
                    '&:hover': located ? { bgcolor: selected ? 'action.selected' : 'action.hover' } : undefined,
                  }}
                >
                  <Typography
                    variant="caption"
                    color={selected ? 'primary.main' : 'text.secondary'}
                    display="block"
                    sx={{ fontWeight: selected ? 700 : 400 }}
                  >
                    {who}
                    {teamLabel(u) ? ` · ${teamLabel(u)}` : ''}
                    {u.started_at ? ` · ${formatTime(u.started_at)}` : ''}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: u.transcript ? 'text.primary' : 'text.disabled', wordBreak: 'break-word' }}
                  >
                    {u.transcript || '(no transcript)'}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default PttCaptionFeed;
